import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Users, Clock, CheckCircle, XCircle, Eye, Filter, Home, Hotel } from 'lucide-react';
import { useGetUserBookingsQuery, useCancelBookingMutation } from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import toast from 'react-hot-toast';
import Navbar from '../../components/common/Navbar';

const MyStayBookings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'confirmed' | 'cancelled' | 'completed'>('all');
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  const { data: bookingsResponse, isLoading } = useGetUserBookingsQuery({
    page: 1,
    limit: 50,
    status: statusFilter === 'all' ? undefined : statusFilter,
  }, {
    skip: !user,
  });

  const [cancelBooking] = useCancelBookingMutation();

  const bookings = bookingsResponse?.data || [];

  const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

  const formatDateTime = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return (
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Confirmed
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="danger" className="flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Cancelled
          </Badge>
        );
      case 'completed':
        return (
          <Badge variant="info" className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Completed
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="warning" className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pending
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;

    setIsCancelling(true);
    try {
      const result = await cancelBooking({
        booking_id: selectedBooking.booking_id,
        reason: cancellationReason || undefined,
      }).unwrap();

      if (result?.success) {
        toast.success('Booking cancelled successfully');
        setShowCancelModal(false);
        setSelectedBooking(null);
        setCancellationReason('');
      } else {
        toast.error(result?.message || 'Failed to cancel booking');
      }
    } catch (error: any) {
      toast.error(error?.data?.message || error?.message || 'Failed to cancel booking. Please try again.');
      console.error('Cancel booking error:', error);
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">My Bookings for Stay</h1>
            <p className="text-gray-600">View and manage your apartment and hotel bookings</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filter by Status:</span>
            {(['all', 'pending', 'confirmed', 'cancelled', 'completed'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </Card>

        {/* Bookings List */}
        {bookings.length === 0 ? (
          <Card className="p-12 text-center">
            <Home className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Stay Bookings Found</h3>
            <p className="text-gray-600 mb-6">
              {statusFilter === 'all'
                ? "You haven't made any apartment or hotel bookings yet."
                : `No ${statusFilter} bookings found.`}
            </p>
            <Button onClick={() => navigate('/apartments')}>Browse Apartments</Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <Card key={booking.booking_id} className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Home className="w-5 h-5 text-gray-600" />
                          <h3 className="text-lg font-semibold text-gray-900">
                            {booking.apartment?.name || 'N/A'}
                          </h3>
                        </div>
                        {booking.booking_reference && (
                          <p className="text-sm text-gray-600">
                            Reference: <strong>{booking.booking_reference}</strong>
                          </p>
                        )}
                      </div>
                      {getStatusBadge(booking.status)}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <label className="text-xs font-medium text-gray-600">Check-in</label>
                        <p className="text-sm text-gray-900 font-medium">{formatDate(booking.check_in_date)}</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">Check-out</label>
                        <p className="text-sm text-gray-900 font-medium">{formatDate(booking.check_out_date)}</p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">Guests</label>
                        <p className="text-sm text-gray-900 flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {booking.guests}
                        </p>
                      </div>
                      {booking.apartment?.location && (
                        <div>
                          <label className="text-xs font-medium text-gray-600">Location</label>
                          <p className="text-sm text-gray-900 flex items-center gap-1 truncate">
                            <MapPin className="w-4 h-4" />
                            {booking.apartment.location}
                          </p>
                        </div>
                      )}
                    </div>
                    {booking.cancellation_reason && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-sm text-gray-600">
                          <strong>Cancellation Reason:</strong> {booking.cancellation_reason}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedBooking(booking);
                        setShowDetailModal(true);
                      }}
                      className="flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </Button>
                    {booking.status === 'confirmed' || booking.status === 'pending' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedBooking(booking);
                          setShowCancelModal(true);
                        }}
                        className="flex items-center gap-2 text-red-600 hover:text-red-700"
                      >
                        <XCircle className="w-4 h-4" />
                        Cancel
                      </Button>
                    ) : null}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Booking Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedBooking(null);
        }}
        title="Booking Details"
      >
        {selectedBooking && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Property</label>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {selectedBooking.apartment?.name || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Status</label>
              <div className="mt-1">
                {getStatusBadge(selectedBooking.status)}
              </div>
            </div>
            {selectedBooking.booking_reference && (
              <div>
                <label className="text-sm font-medium text-gray-600">Booking Reference</label>
                <p className="text-sm text-gray-900 mt-1 font-mono">
                  {selectedBooking.booking_reference}
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Check-in Date</label>
                <p className="text-sm text-gray-900 mt-1">
                  {formatDate(selectedBooking.check_in_date)}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Check-out Date</label>
                <p className="text-sm text-gray-900 mt-1">
                  {formatDate(selectedBooking.check_out_date)}
                </p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Number of Guests</label>
              <p className="text-sm text-gray-900 mt-1">
                {selectedBooking.guests} {selectedBooking.guests === 1 ? 'guest' : 'guests'}
              </p>
            </div>
            {selectedBooking.apartment?.location && (
              <div>
                <label className="text-sm font-medium text-gray-600">Location</label>
                <p className="text-sm text-gray-900 mt-1 flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {selectedBooking.apartment.location}
                </p>
              </div>
            )}
            {selectedBooking.apartment?.description && (
              <div>
                <label className="text-sm font-medium text-gray-600">Description</label>
                <p className="text-sm text-gray-900 mt-1">
                  {selectedBooking.apartment.description}
                </p>
              </div>
            )}
            {selectedBooking.created_at && (
              <div>
                <label className="text-sm font-medium text-gray-600">Booked On</label>
                <p className="text-sm text-gray-900 mt-1">
                  {formatDateTime(selectedBooking.created_at)}
                </p>
              </div>
            )}
            {selectedBooking.cancellation_reason && (
              <div>
                <label className="text-sm font-medium text-gray-600">Cancellation Reason</label>
                <p className="text-sm text-gray-900 mt-1 p-3 bg-red-50 rounded-lg">
                  {selectedBooking.cancellation_reason}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Cancel Booking Modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setSelectedBooking(null);
          setCancellationReason('');
        }}
        title="Cancel Booking"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to cancel this booking? This action cannot be undone.
          </p>
          {selectedBooking && (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-900 mb-2">Booking Details:</p>
              <p className="text-sm text-gray-600">{selectedBooking.apartment?.name}</p>
              <p className="text-sm text-gray-600">
                {formatDate(selectedBooking.check_in_date)} - {formatDate(selectedBooking.check_out_date)}
              </p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cancellation Reason (Optional)
            </label>
            <textarea
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              placeholder="Please provide a reason for cancellation..."
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowCancelModal(false);
                setSelectedBooking(null);
                setCancellationReason('');
              }}
              className="flex-1"
            >
              Keep Booking
            </Button>
            <Button
              type="button"
              onClick={handleCancelBooking}
              disabled={isCancelling}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              {isCancelling ? 'Cancelling...' : 'Cancel Booking'}
            </Button>
          </div>
        </div>
      </Modal>
      </div>
    </>
  );
};

export default MyStayBookings;
